import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, switchMap, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';

import { Lead } from '../../../../shared/lead';
import { BrandService } from '../../../../services/brand.service';
import { FruHttpService } from '../../../../services/fru-http.service';
import { OfficeStatus } from '../../../../shared/office-status';
import { ConsentAttemptTelemetryService } from '../../../../services/consent-attempt-telemetry.service';

@Component({
    selector: 'email-form-email-verification',
    templateUrl: './email-verification.component.html',
    styleUrls: ['./email-verification.component.scss'],
    standalone: false
})
export class EmailVerificationComponent implements OnInit {
  @Input() lead: Lead;
	public officeStatus: OfficeStatus;
	public msg: string;
	public currentYear: number = 2023;
	public encryptedLeadId: string;
	public loadErrorMessage: string = '';

  //FRU-23038 diagnostics: only the consent-update flow (context=update) mirrors the
  //one-to-one load diagnostics; a plain verification link stays telemetry-free.
  //Guards mirror one-to-one so a single lead-load failure is recorded once per attempt
  //(the catchError and the null-response path can both observe the same failure).
  private consentUpdateFlow = false;
  private leadLoadFailureSent = false;
  private leadLoadResponseReceived = false;

  constructor(private fruHttp: FruHttpService, private route: ActivatedRoute, private router: Router, public brandService: BrandService, private consentTelemetry: ConsentAttemptTelemetryService) { }

  ngOnInit() {
    let d = new Date();
    this.currentYear = d.getFullYear();

    this.route.queryParams.subscribe(params => {
      this.msg = (params.utm_medium == 'click-expired')
        ? 'please verify your information to get updated rates.'
        : 'as we discussed in my message, please verify the information below is still accurate.';

      this.fruHttp
        .getOfficeStatus(params.utm_source || 'consumer_campaign')
        .subscribe(officeStatus => this.officeStatus = officeStatus)
    });

    //The router reuses this component when the stale-link redirect swaps the
    //lead id, so the id must come from paramMap: the snapshot would keep
    //pointing at the old lead
		this.route.paramMap.pipe(
			tap(paramMap => {
				this.lead = undefined;
				this.loadErrorMessage = '';
				this.leadLoadFailureSent = false;
				this.leadLoadResponseReceived = false;
				this.encryptedLeadId = paramMap.get('id');
				this.consentUpdateFlow = String(this.route.snapshot.queryParams.context || '') === 'update';
				if (this.encryptedLeadId) {
					Sentry.setUser({ id: this.encryptedLeadId });
					if (this.consentUpdateFlow) {
						//FRU-23038: a (restored) attempt per encrypted lead id; a stale-link
						//redirect to a new id starts a fresh attempt. Diagnostics only.
						const src = String(this.route.snapshot.queryParams.src || '').toLowerCase();
						this.consentTelemetry.startAttempt(this.encryptedLeadId, ['sms', 'email'].includes(src) ? src : 'web');
						this.recordConsentUpdateEvent({ stage: 'application_started', outcome: 'success' });
					}
				} else {
					Sentry.setUser(null);
				}
			}),
			switchMap(() => this.getLead(this.encryptedLeadId))
		).subscribe(lead => this.handleLeadResponse(lead));
  }

  getLead(id: string): Observable<Lead> {
    const params: any = this.route.snapshot.queryParams;
    const leadType: string = params.type || '';
    const adNetwork: string = params.utm_source || 'consumer_campaign';
    //A consent-update link (context=update) must be checked for staleness
    //before the form is shown or tokens are captured
    const consentUpdate: boolean = params.context == 'update';

    if (!id) {
      this.redirectToMainForm();
      return EMPTY;
    }

    if (!this.consentUpdateFlow) {
      //A plain verification link keeps the original single-shot pipeline and
      //emits no consent-attempt telemetry.
      return this.fruHttp.getLead(id, leadType, adNetwork, false, consentUpdate);
    }

    //FRU-23038: mirror the one-to-one load diagnostics — lead_load_started, then a
    //single named failure (http_error via catchError, network/unknown via the null
    //path). Never changes what the consumer sees. Do not timeout or retry here:
    //a slow valid response must still reach the component instead of being
    //converted to null by diagnostics.
    this.recordConsentUpdateEvent({ stage: 'lead_load_started', outcome: 'success' });

    return this.fruHttp.getLead(id, leadType, adNetwork, false, consentUpdate).pipe(
      //an EMPTY completion (status 0 in FruHttpService.getLead) emits nothing, so
      //this marks that a real response arrived and lets the null path name the failure
      tap(() => { this.leadLoadResponseReceived = true; }),
      defaultIfEmpty(null),
      catchError(err => {
        this.recordLeadLoadFailure(err);
        return of(null);
      })
    );
  }

  //FRU-23038 diagnostics only — never changes what the consumer sees.
  private recordLeadLoadFailure(error: any): void {
    this.leadLoadFailureSent = true;
    const isTimeout = !!(error && (error.name === 'TimeoutError' || /timeout/i.test(String(error && error.message || ''))));
    const status = (error && typeof error.status === 'number') ? error.status : 0;
    this.recordConsentUpdateEvent({
      stage: 'lead_load_failed',
      outcome: 'failure',
      reason: isTimeout ? 'timeout' : 'http_error',
      httpStatus: status,
    });
  }

  handleLeadResponse(lead: Lead) {
    if (lead === null) {
      if (this.consentUpdateFlow && !this.leadLoadFailureSent) {
        //FRU-23038: null without a recorded error means the request completed EMPTY
        //(status 0 / network) or the backend answered nothing — record it once, then
        //keep the existing redirect.
        this.recordConsentUpdateEvent({
          stage: 'lead_load_failed',
          outcome: 'failure',
          reason: this.leadLoadResponseReceived ? 'unknown' : 'network',
          httpStatus: 0,
        });
      }
      this.redirectToMainForm();
      return;
    }

    if (lead.status === 'error') {
      if (this.consentUpdateFlow) {
        this.recordConsentUpdateEvent({
          stage: 'lead_load_failed',
          outcome: 'failure',
          reason: lead.reason === 'ambiguous_consent_update_link' ? 'ambiguous_link' : (lead.reason || 'error'),
        });
      }
      this.loadErrorMessage = 'We could not safely load this verification link. Please refresh the link and try again.';
      return;
    }

    //A stale consent-update link points at an old request: swap the URL to
    //the current lead before the form is shown or submitted, so Jornaya and
    //TrustedForm tokens are generated for the current request
    if (lead.status === 'redirect' && lead.reason === 'stale_consent_update_link' && lead.leadId) {
      if (this.consentUpdateFlow) {
        this.recordConsentUpdateEvent({
          stage: 'stale_link_redirect',
          outcome: 'unknown',
          reason: 'stale_consent_update_link',
          requestId: lead.leadId,
          originalRequestId: this.encryptedLeadId,
          metadata: { original_encrypted_lead_id: this.encryptedLeadId },
        });
      }
      this.router.navigate(['/email-verification', lead.leadId], { queryParams: this.route.snapshot.queryParams });
      return;
    }

    this.lead = lead;
    const params: any = this.route.snapshot.queryParams;
    if (params.utm_source) {
      this.lead.adnetwork = params.utm_source;
    }
    this.lead.medium = params.utm_medium || '';
		this.lead.campaign = this.encryptedLeadId; // Store the encrypted id
    this.lead.keyword = params.utm_keyword || '';
    this.lead.content = params.utm_content || '';
    this.lead.network = params.network || '';
    this.lead.gclid = params.gclid || '';

    if (this.consentUpdateFlow) {
      this.recordConsentUpdateEvent({ stage: 'lead_load_success', outcome: 'success' });
      this.recordConsentUpdateEvent({ stage: 'form_ready', outcome: 'success' });
    }
  }

  private recordConsentUpdateEvent(event: any): void {
    this.consentTelemetry.record(this.encryptedLeadId, { ...event, source: 'consent_update' });
  }

  redirectToMainForm() {
    const isKarmaRunning = typeof window !== 'undefined' && (window as any).__karma__;
    if (!isKarmaRunning) {
      this.fruHttp.redirectMainForm();
    }
  }
}
